import { DateTime } from "luxon";
import type { GetBookingFloorParams } from "../../Booking-Form/thunks/booking.thunks";
import {
  BookingType,
  Frequence,
  type NotSerializedBookingInputs
} from "../../Booking-Form/typings/booking-inputs";
import type {
  BookingScheduleInterface,
  ConferenceBookingDto
} from "../../Booking-Form/typings/booking.types";
import { isScheduleMobileWorkingLocation } from "../../../components/Schedule/schedule-event-location.component";
import {
  CONFERENCE_ZONE_TYPE_ID,
  ELECTRIC_CHARGING_PLACE_TYPE_ID,
  PARKING_PLACE_TYPE_ID,
  WORKPLACE_PLACE_TYPE_ID
} from "../../Booking-Form/typings/place-and-zone-type-ids.constant";
import type { PlaceBookingDto } from "../../Booking-Form/typings/booking.types";
import { defaultRecurrenceWeekdays } from "../../Booking-Form/typings/shared/weekdays";
import type { TeamMember } from "../../Booking-Form/typings/team-member";
import type { ConnectedBookingData } from "../../Booking-Form/typings/connected-bookingData";
import type { FloorAvailability } from "../../FloorManager/typings/floor-inventory.entity";
import type { ISeatStatus } from "../../../components/FacilityManager/Domain/Types/FloorPlan/SeatStatus.type";
import { getPlanDateRangeOrNull } from "../../Booking-Form/functions/floor.functions";
import type { UserInformation } from "../../Login/typings/login.types";
import {
  generateInitial,
  genInitialByColleaguesBooking
} from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/Functions/SelectFloorPlanView.functions";
import type { IFloorPayload } from "../../../components/FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import {
  handleToBookingServiceDto,
  transformBookingUsers
} from "../../../components/BookingForm/functions/form-confirm.functions";
import {
  buildConferenceVisitorRegistrationDtos,
  conferenceRoomLocationFromInputs
} from "../../../components/BookingForm/functions/team-visitor-invitation.functions";
import {
  ColleagueDeskClickKind,
  type ColleagueFloorChip,
  type ColleagueFloorConferenceBookBlock,
  type ColleagueFloorConferenceDraft,
  type ColleagueWorkplaceFloorSession
} from "./colleague-workplace-floor.types";

/**
 * Identity of the outline currently drawn so a floor switch can drop a stale Pixi scene.
 * Query strings are ignored so a refreshed signed outline link does not redraw the same floor.
 */
export function colleagueFloorSceneKey(
  floorPlan?: Pick<IFloorPayload, "floorInventoryId" | "outlineUrl">
): string {
  if (floorPlan == null) {
    return "";
  }
  const outlinePath = floorPlan.outlineUrl?.split("?")[0] ?? "";
  return `${floorPlan.floorInventoryId}:${outlinePath}`;
}

/** Position of the logged-in user's booked object on this floor, when that object is drawn here. */
export function colleagueFloorBookedObjectPosition(
  floorPlan: Pick<IFloorPayload, "places"> | undefined,
  highlightInventoryId: number | undefined
): { x: number; y: number } | undefined {
  if (floorPlan == null || highlightInventoryId == null) {
    return undefined;
  }

  const bookedPlace = floorPlan.places.find(place => {
    const inventoryId = place.inventoryId || place.inventory?.id;
    return inventoryId === highlightInventoryId;
  });
  return bookedPlace?.position;
}

export const emptyColleagueWorkplaceFloorSession: ColleagueWorkplaceFloorSession = {
  open: false,
  date: "",
  startTime: "08:00",
  endTime: "17:00"
};

/** Floor availability request for one site and day. Workplace unless a parking, charging, or conference booking opened the plan. */
export function buildColleagueFloorAvailabilityArgs(args: {
  locationInventoryId: number;
  user: Pick<TeamMember, "userId" | "firstName" | "surname">;
  date: string;
  startTime: string;
  endTime: string;
  bookingType?: BookingType;
}): GetBookingFloorParams {
  return {
    users: [
      {
        userId: args.user.userId,
        firstName: args.user.firstName,
        surname: args.user.surname,
        isExternal: false
      }
    ],
    startDate: args.date,
    endDate: args.date,
    startTime: args.startTime,
    endTime: args.endTime,
    bookingType: args.bookingType ?? BookingType.WORKPLACE,
    locationInventoryId: args.locationInventoryId,
    description: ""
  };
}

/**
 * Location and floor taken from a workplace, parking, charging, or conference schedule.
 * Used when Show place or Show Zone opens the colleague plan from that booking.
 */
export type ColleagueFloorScheduleAnchor = {
  /** Booked object to mark. Also used to find its floor in the availability lists. */
  highlightInventoryId: number;
  /** Site of the booking. Absent when the schedule has no location id. */
  locationInventoryId?: number;
  /** Site name shown in the location control. */
  locationName?: string;
  /** Floor id from the booked place. Absent for a workplace desk, which is found by inventory id. */
  preferredFloorId?: number;
  /** Floor name from the schedule, used when the place has no floor id. */
  preferredFloorName?: string;
  /** Availability request mode so parking, charging, or conference floors are returned. */
  availabilityBookingType: BookingType;
};

function positiveInventoryId(value: number | null | undefined): number | undefined {
  if (value != null && value > 0) {
    return value;
  }
  return undefined;
}

function colleagueFloorPlaceAvailabilityType(
  bookingType: BookingType | string
): BookingType | undefined {
  if (bookingType === BookingType.PARKINGPLACE) {
    return BookingType.PARKINGPLACE;
  }
  if (bookingType === BookingType.ELECTRICCHARGINGSTATIONPLACE) {
    return BookingType.ELECTRICCHARGINGSTATIONPLACE;
  }
  if (bookingType === BookingType.WORKPLACE) {
    return BookingType.WORKPLACE;
  }
  if (bookingType === BookingType.CONFERENCEZONE) {
    return BookingType.CONFERENCEZONE;
  }
  return undefined;
}

function scheduleLocationInventoryId(schedule: BookingScheduleInterface): number | undefined {
  const fromSchedule = positiveInventoryId(schedule.locationInventoryId);
  if (fromSchedule != null) {
    return fromSchedule;
  }

  const inventory = schedule.bookingInventory;
  if (inventory == null) {
    return undefined;
  }
  if ("locationInventoryId" in inventory) {
    const fromInventory = positiveInventoryId(inventory.locationInventoryId);
    if (fromInventory != null) {
      return fromInventory;
    }
  }
  if ("locationInventory" in inventory) {
    return positiveInventoryId(inventory.locationInventory?.id);
  }
  return undefined;
}

function scheduleFloorInventoryId(
  inventory: BookingScheduleInterface["bookingInventory"]
): number | undefined {
  if (inventory == null) {
    return undefined;
  }
  if ("floorInventoryId" in inventory) {
    const fromId = positiveInventoryId(inventory.floorInventoryId);
    if (fromId != null) {
      return fromId;
    }
  }
  if ("floorInventory" in inventory) {
    return positiveInventoryId(inventory.floorInventory?.id);
  }
  return undefined;
}

/**
 * Workplace, parking, charging, or conference booking that should open its own site and floor.
 * Mobile working and other booking types return undefined so the favorite site is used.
 */
export function colleagueFloorAnchorFromSchedule(
  schedule: BookingScheduleInterface
): ColleagueFloorScheduleAnchor | undefined {
  if (isScheduleMobileWorkingLocation(schedule)) {
    return undefined;
  }

  const availabilityBookingType = colleagueFloorPlaceAvailabilityType(schedule.bookingType);
  if (availabilityBookingType == null) {
    return undefined;
  }

  const locationInventoryId = scheduleLocationInventoryId(schedule);
  const rawFloorName: unknown = schedule.floorName;
  const floorName = typeof rawFloorName === "string" ? rawFloorName.trim() : "";
  const isWorkplace = availabilityBookingType === BookingType.WORKPLACE;

  return {
    highlightInventoryId: schedule.bookingInventoryId,
    locationInventoryId,
    locationName: schedule.locationName,
    preferredFloorId: isWorkplace ? undefined : scheduleFloorInventoryId(schedule.bookingInventory),
    preferredFloorName: isWorkplace || floorName === "" ? undefined : floorName,
    availabilityBookingType
  };
}

export function colleagueFloorPlanIsoRange(
  date: string,
  startTime: string,
  endTime: string
): { start: string; end: string } | null {
  return getPlanDateRangeOrNull(date, startTime, date, endTime);
}

/** Workplace inventory name for occupancy-table title (`inventory.name`, not uid). */
export function colleagueFloorInventoryName(
  floorPlan: Pick<IFloorPayload, "places"> | undefined,
  inventoryId: number | undefined
): string | undefined {
  if (inventoryId == null) {
    return undefined;
  }
  const place = floorPlan?.places.find(
    workplace => workplace.inventoryId === inventoryId || workplace.inventory?.id === inventoryId
  );
  const name = place?.inventory?.name?.trim();
  return name === "" ? undefined : name;
}

/**
 * Query window for GET /booking-schedule/place from a colleague-floor occupancy click.
 * Date-only `endDate` is start-of-day UTC (`LessThanOrEqual`), so the selected day must
 * be sent as the following calendar day — same convention as Dashboard2 week schedules.
 */
export function colleagueFloorScheduleQueryRange(
  date: string
): { startDate: string; endDate: string } | null {
  const day = DateTime.fromISO(date);
  if (!day.isValid) {
    return null;
  }
  return {
    startDate: day.toFormat("yyyy-MM-dd"),
    endDate: day.plus({ days: 1 }).toFormat("yyyy-MM-dd")
  };
}

/** Chip label data: floor name + how many colleagues booked on that floor. */
export function toColleagueFloorChips(floors: FloorAvailability[]): ColleagueFloorChip[] {
  return floors.map(floor => ({
    id: floor.id,
    name: floor.name,
    colleagueCount: floor.connectedBookingData?.length ?? 0
  }));
}

function floorListsInventory(floor: FloorAvailability, inventoryId: number): boolean {
  const listed = new Set([
    ...(floor.occupiedInventoryIds ?? []),
    ...(floor.userOccupiedInventoryIds ?? []),
    ...(floor.availableInventoryIds ?? [])
  ]);
  if (listed.has(inventoryId)) {
    return true;
  }

  return floor.connectedBookingData?.some(row => row.bookingInventoryId === inventoryId) ?? false;
}

/**
 * Floor to open for the type now on screen.
 * The highlighted object wins when it is on one of these floors.
 * A highlight that is not on them is leftover: it does not select a floor, and neither does
 * the old floor id. A floor name is never a match, because the same name can be a different floor.
 * When this returns undefined, the caller uses a favorite of this type, then the first floor.
 */
export function colleagueFloorAnchorId(
  floors: FloorAvailability[],
  highlightInventoryId?: number,
  preferredFloorId?: number
): number | undefined {
  if (highlightInventoryId != null) {
    return floors.find(floor => floorListsInventory(floor, highlightInventoryId))?.id;
  }

  if (
    preferredFloorId != null &&
    preferredFloorId > 0 &&
    floors.some(floor => floor.id === preferredFloorId)
  ) {
    return preferredFloorId;
  }

  return undefined;
}

/**
 * Result of walking the loaded floors for a saved favorite of the type on screen.
 * Used when the highlighted object is not on those floors.
 */
export type ColleagueFavoriteFloorLookup = {
  /** Floor that already has a favorite of this type. Absent when none of the loaded floors do. */
  floorId?: number;
  /** An earlier floor's favorites are still loading, so the choice is not final. */
  isPending: boolean;
};

/**
 * First floor whose favorite list is non-empty.
 * The caller passes only favorites of the type on screen.
 * `undefined` in `favoriteLists` means that floor's favorites are not loaded yet.
 * An empty list means that floor has no favorite of this type.
 */
export function colleagueFavoriteFloorLookup(
  floors: Array<{ id: number }>,
  favoriteLists: ReadonlyArray<readonly unknown[] | undefined>
): ColleagueFavoriteFloorLookup {
  for (let index = 0; index < floors.length; index += 1) {
    const favorites = favoriteLists[index];
    if (favorites == null) {
      return { isPending: true };
    }
    if (favorites.length > 0) {
      return { floorId: floors[index].id, isPending: false };
    }
  }

  return { isPending: false };
}

/**
 * Seat lists for the colleague dialog. Partial desks are painted as pies.
 * Wizard occupancy stays binary because it does not set `partialList`.
 */
export function buildColleagueWorkplaceSeatStatus(currentFloor: FloorAvailability): ISeatStatus {
  const partialIds = currentFloor.partiallyOccupiedInventoryIds ?? [];
  const partialSet = new Set(partialIds);
  const blocked = new Set(currentFloor.blockedInventoryIds ?? []);

  return {
    availableList: currentFloor.availableInventoryIds.filter(id => !blocked.has(id)),
    occupiedList: currentFloor.occupiedInventoryIds,
    disableList: currentFloor.disabledInventoryIds,
    restrictedList: currentFloor.forbiddenInventoryIds,
    userOccupiedList: currentFloor.userOccupiedInventoryIds ?? [],
    blockedList: [...blocked],
    partialList: [...partialSet]
  };
}

export function isColleagueDeskBookable(
  inventoryId: number | undefined,
  seatStatus: ISeatStatus
): boolean {
  return resolveColleagueDeskClickKind(inventoryId, seatStatus) === ColleagueDeskClickKind.BOOK;
}

/**
 * Free objects book. Occupied, partial, and the person's own full-window booking
 * open who is already there. The own booking stays on the free list from the server.
 */
export function resolveColleagueDeskClickKind(
  inventoryId: number | undefined,
  seatStatus: ISeatStatus
): ColleagueDeskClickKind {
  if (inventoryId == null) {
    return ColleagueDeskClickKind.NONE;
  }

  const available = new Set(seatStatus.availableList);
  const partial = new Set(seatStatus.partialList ?? []);
  const occupied = new Set(seatStatus.occupiedList);
  const userOccupied = new Set(seatStatus.userOccupiedList ?? []);
  const forbidden = new Set(seatStatus.restrictedList);
  const disabled = new Set(seatStatus.disableList);
  const blocked = new Set(seatStatus.blockedList ?? []);

  if (forbidden.has(inventoryId) || disabled.has(inventoryId) || blocked.has(inventoryId)) {
    return ColleagueDeskClickKind.NONE;
  }

  if (
    partial.has(inventoryId) ||
    userOccupied.has(inventoryId) ||
    (occupied.has(inventoryId) && !available.has(inventoryId))
  ) {
    return ColleagueDeskClickKind.OCCUPANCY;
  }

  if (available.has(inventoryId)) {
    return ColleagueDeskClickKind.BOOK;
  }

  return ColleagueDeskClickKind.NONE;
}

/** Occupancy rows for one desk, including the login user when the API marks the seat as theirs. */
export function occupancyBookingsForDesk(args: {
  inventoryId: number;
  connectedBookingData: ConnectedBookingData[];
  currentUser: Pick<ConnectedBookingData, "userId" | "firstName" | "lastName" | "email">;
  isUserOccupied: boolean;
  /** The login user's own bookings. Hover uses these hours, not the floor window. */
  ownBookings?: ReadonlyArray<{
    bookingInventoryId: number;
    bookingId?: number;
    start: string;
    end: string;
  }>;
}): ConnectedBookingData[] {
  const ownOnDesk = (args.ownBookings ?? []).filter(
    booking => booking.bookingInventoryId === args.inventoryId
  );
  const bookings = args.connectedBookingData
    .filter(row => row.bookingInventoryId === args.inventoryId)
    .map(row => {
      if (row.userId !== args.currentUser.userId || (row.start && row.end)) {
        return row;
      }
      const match =
        ownOnDesk.find(booking => row.bookingId == null || booking.bookingId === row.bookingId) ??
        ownOnDesk[0];
      if (!match) {
        return row;
      }
      return {
        ...row,
        start: match.start,
        end: match.end,
        bookingId: row.bookingId ?? match.bookingId
      };
    });
  if (args.isUserOccupied && !bookings.some(row => row.userId === args.currentUser.userId)) {
    if (ownOnDesk.length > 0) {
      return [
        ...bookings,
        ...ownOnDesk.map(booking => ({
          userId: args.currentUser.userId,
          firstName: args.currentUser.firstName,
          lastName: args.currentUser.lastName,
          email: args.currentUser.email,
          bookingInventoryId: args.inventoryId,
          bookingId: booking.bookingId,
          start: booking.start,
          end: booking.end
        }))
      ];
    }
    return [
      ...bookings,
      {
        userId: args.currentUser.userId,
        firstName: args.currentUser.firstName,
        lastName: args.currentUser.lastName,
        email: args.currentUser.email,
        bookingInventoryId: args.inventoryId
      }
    ];
  }
  return bookings;
}

/** True when several occupancy rows exist — partial vs full seat paint does not matter. */
export function shouldOpenColleagueOccupancyTable(bookings: ConnectedBookingData[]): boolean {
  return bookings.length > 1;
}

export function formatColleagueOccupancyTimeRange(
  row: Pick<ConnectedBookingData, "start" | "end">,
  timeFormat: string
): string {
  if (!row.start || !row.end) {
    return "";
  }
  const start = DateTime.fromISO(row.start);
  const end = DateTime.fromISO(row.end);
  if (!start.isValid || !end.isValid) {
    return "";
  }
  return `${start.toFormat(timeFormat)}–${end.toFormat(timeFormat)}`;
}

/** True when the login user occupies this desk for the current availability window (API only). */
export function isColleagueUserOccupiedDesk(
  inventoryId: number | undefined,
  seatStatus: ISeatStatus
): boolean {
  if (inventoryId == null) {
    return false;
  }

  return seatStatus.userOccupiedList?.includes(inventoryId) ?? false;
}

/**
 * The logged-in user's own workplace, parking, or charging booking for this day.
 * The session keeps this id when availability omits it. The mark is painted only when
 * the floors for the type on screen list that same object.
 */
export function isColleagueFloorOwnBookingHighlight(
  highlightInventoryId: number | undefined,
  occupantInventoryIds: readonly number[] | undefined
): boolean {
  return (
    highlightInventoryId != null && (occupantInventoryIds?.includes(highlightInventoryId) ?? false)
  );
}

/**
 * Id to paint and zoom as the pale yellow booked object.
 * Only an object listed on the floors for the type now on screen. A leftover id from
 * another type is not painted, even when some other object uses the same number.
 */
export function colleagueFloorDisplayHighlightId(args: {
  highlightInventoryId: number | undefined;
  floors: FloorAvailability[];
  isLoading: boolean;
}): number | undefined {
  const { highlightInventoryId, floors, isLoading } = args;
  if (highlightInventoryId == null || isLoading || floors.length === 0) {
    return undefined;
  }

  const anchorFloorId = colleagueFloorAnchorId(floors, highlightInventoryId);
  return anchorFloorId != null ? highlightInventoryId : undefined;
}

/**
 * Session highlight is valid only when the logged-in user is on that desk in the
 * current floor availability response (any floor at the site).
 */
export function isColleagueSessionHighlightValid(
  floors: FloorAvailability[],
  highlightInventoryId: number | undefined,
  userId: string
): boolean {
  if (highlightInventoryId == null || floors.length === 0) {
    return false;
  }

  return floors.some(floor => {
    if (floor.userOccupiedInventoryIds?.includes(highlightInventoryId)) {
      return true;
    }

    return (
      floor.connectedBookingData?.some(
        row => row.userId === userId && row.bookingInventoryId === highlightInventoryId
      ) ?? false
    );
  });
}

const OCCUPANT_MARK_PLACE_TYPE_IDS = new Set<number>([
  WORKPLACE_PLACE_TYPE_ID,
  PARKING_PLACE_TYPE_ID,
  ELECTRIC_CHARGING_PLACE_TYPE_ID
]);

/** Workplace, parking, and electric charging objects can show a booker's initials or photo. */
export function isColleagueFloorOccupantPlaceType(placeTypeId: number | undefined): boolean {
  return placeTypeId != null && OCCUPANT_MARK_PLACE_TYPE_IDS.has(placeTypeId);
}

/**
 * Click for a place on the colleague floor.
 * A free desk, bay, or charger opens booking. A partly or fully taken one shows who is
 * already there and does not book the hours that are still free.
 */
export function colleagueFloorPlaceClickKind(
  placeTypeId: number | undefined,
  inventoryId: number | undefined,
  seatStatus: ISeatStatus
): ColleagueDeskClickKind {
  if (!isColleagueFloorOccupantPlaceType(placeTypeId)) {
    return ColleagueDeskClickKind.NONE;
  }

  return resolveColleagueDeskClickKind(inventoryId, seatStatus);
}

/** Booking type posted for a clicked desk, bay, or charger. An unknown type stays a desk. */
export function colleagueFloorPlaceBookingType(placeTypeId: number | undefined): BookingType {
  if (placeTypeId === PARKING_PLACE_TYPE_ID) {
    return BookingType.PARKINGPLACE;
  }
  if (placeTypeId === ELECTRIC_CHARGING_PLACE_TYPE_ID) {
    return BookingType.ELECTRICCHARGINGSTATIONPLACE;
  }
  return BookingType.WORKPLACE;
}

/**
 * The zone the person chose to book. A missing id is not a match: before any click the
 * selection is unset, and `undefined === undefined` would open the first nameless zone.
 */
export function colleagueFloorZoneByInventoryId<
  T extends { inventoryId?: number; inventory?: { id?: number } }
>(zones: T[] | undefined, inventoryId: number | undefined): T | undefined {
  if (inventoryId == null || zones == null) {
    return undefined;
  }

  return zones.find(zone => zone.inventory?.id === inventoryId || zone.inventoryId === inventoryId);
}

/**
 * Click for a zone on the colleague floor.
 * A free conference room opens the conference sheet. A taken room shows who is already there.
 * Other zone types, and conference rooms while another booking type is on screen, do nothing.
 */
export function colleagueFloorZoneClickKind(
  zoneTypeId: number | undefined,
  inventoryId: number | undefined,
  seatStatus: ISeatStatus,
  availabilityBookingType: BookingType | undefined
): ColleagueDeskClickKind {
  if (
    availabilityBookingType !== BookingType.CONFERENCEZONE ||
    zoneTypeId !== CONFERENCE_ZONE_TYPE_ID
  ) {
    return ColleagueDeskClickKind.NONE;
  }

  return resolveColleagueDeskClickKind(inventoryId, seatStatus);
}

/**
 * Book stays disabled until services have been checked, the title is filled,
 * and a required service is chosen.
 */
export function colleagueFloorConferenceBookBlockReason(args: {
  subject: string;
  servicesLoading: boolean;
  mandatoryServiceMissing: boolean;
}): ColleagueFloorConferenceBookBlock | undefined {
  if (args.servicesLoading) {
    return "loading";
  }
  if (args.subject.trim() === "") {
    return "title";
  }
  if (args.mandatoryServiceMissing) {
    return "service";
  }
  return undefined;
}

const BAY_OR_CHARGER_PLACE_TYPE_IDS = new Set<number>([
  PARKING_PLACE_TYPE_ID,
  ELECTRIC_CHARGING_PLACE_TYPE_ID
]);

/**
 * Confirm button and success message for a colleague-floor booking.
 * A desk says "desk". A bay or charger says "place".
 */
export type ColleagueFloorBookConfirmCopy = {
  /** Confirm button label. */
  confirmKey: "_colleagueFloor_confirmBook" | "_colleagueFloor_confirmBookPlace";
  /** Snackbar after the booking is saved. */
  successKey: "_colleagueFloor_bookSuccess" | "_colleagueFloor_bookSuccessPlace";
};

/** Desk copy unless the object is a parking bay or a charger. */
export function colleagueFloorBookConfirmCopy(
  placeTypeId: number | undefined
): ColleagueFloorBookConfirmCopy {
  if (placeTypeId != null && BAY_OR_CHARGER_PLACE_TYPE_IDS.has(placeTypeId)) {
    return {
      confirmKey: "_colleagueFloor_confirmBookPlace",
      successKey: "_colleagueFloor_bookSuccessPlace"
    };
  }

  return {
    confirmKey: "_colleagueFloor_confirmBook",
    successKey: "_colleagueFloor_bookSuccess"
  };
}

export function toHighlightedColleagueUserBookings(
  usersBookedFor: TeamMember[],
  highlightInventoryId?: number
): TeamMember[] {
  const [currentUser] = usersBookedFor;
  if (currentUser == null || highlightInventoryId == null) {
    return usersBookedFor;
  }

  return [{ ...currentUser, bookingInventoryId: highlightInventoryId }];
}

export function generateColleagueFloorIcon(args: {
  highlightedUserBookings: TeamMember[];
  userInformation: UserInformation | undefined;
  searchedColleagues: ConnectedBookingData[];
  inventoryId: number | undefined;
  isUserOccupiedPlace: boolean;
}): string | undefined {
  if (args.inventoryId == null) {
    return undefined;
  }

  const colleaguesOnDesk = args.searchedColleagues.filter(
    colleague => colleague.bookingInventoryId === args.inventoryId
  );
  if (colleaguesOnDesk.length > 0) {
    return genInitialByColleaguesBooking(colleaguesOnDesk);
  }

  return generateInitial(
    "booking",
    args.highlightedUserBookings,
    args.userInformation,
    [],
    args.inventoryId,
    args.isUserOccupiedPlace
  );
}

/**
 * True when PlaceIcon can paint the string: two-letter initials, a short "+N" stack, or a fetchable image URL.
 * Profile `photoUrl` is often a storage key (`user-photos/…`). That is longer than a label and is not a URL, so PlaceIcon loads nothing and draws nothing.
 */
export function isDrawableColleagueFloorIcon(icon: string): boolean {
  if (icon.length === 2) {
    return true;
  }
  if (icon.length >= 4 && icon.length <= 6 && icon.includes("+")) {
    return true;
  }
  return icon.startsWith("http://") || icon.startsWith("https://") || icon.startsWith("data:");
}

function colleagueFloorLoginInitials(
  firstName: string | undefined,
  surname: string | undefined
): string | undefined {
  const first = firstName?.trim();
  const last = surname?.trim();
  if (!first || !last) {
    return undefined;
  }
  return `${first[0]}${last[0]}`.toUpperCase();
}

function isUserPhotoStorageKey(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.startsWith("user-photos/") || trimmed.startsWith("/user-photos/");
}

/** Public object URL: `REACT_APP_BUCKET_URL` + storage key. Empty when the bucket env is unset. */
function toBucketPhotoUrl(storageKey: string): string | undefined {
  const bucketBase = process.env.REACT_APP_BUCKET_URL?.replace(/\/+$/, "");
  if (!bucketBase) {
    return undefined;
  }
  const key = storageKey.replace(/^\/+/, "");
  return `${bucketBase}/${key}`;
}

/**
 * Icon for one colleague-floor desk. Keeps initials and real image URLs.
 * Turns the logged-in user's storage-key photo into a bucket URL so Pixi can load it.
 * Initials are the fallback when `REACT_APP_BUCKET_URL` is unset.
 */
export function toDrawableColleagueFloorIcon(
  generated: string | undefined,
  loginUser: { firstName?: string; surname?: string; photoUrl?: string | null }
): string | undefined {
  if (!generated) {
    return undefined;
  }
  if (isDrawableColleagueFloorIcon(generated)) {
    return generated;
  }
  if (isUserPhotoStorageKey(generated)) {
    const bucketUrl = toBucketPhotoUrl(generated);
    if (bucketUrl) {
      return bucketUrl;
    }
    if (loginUser.photoUrl && generated === loginUser.photoUrl) {
      return colleagueFloorLoginInitials(loginUser.firstName, loginUser.surname);
    }
    return undefined;
  }
  if (loginUser.photoUrl && generated === loginUser.photoUrl) {
    return (
      toBucketPhotoUrl(generated) ??
      colleagueFloorLoginInitials(loginUser.firstName, loginUser.surname)
    );
  }
  return undefined;
}

export function colleagueSearchLabel(row: ConnectedBookingData): string {
  const name = `${row.firstName} ${row.lastName}`.trim();
  return name === "" ? row.email : name;
}

/** Normalize Mobiscroll datepicker values to `yyyy-MM-dd` for the colleague floor session. */
export function toColleagueFloorDateInputValue(value: unknown): string | null {
  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toFormat("yyyy-MM-dd");
  }
  if (typeof value === "string") {
    const parsed = DateTime.fromISO(value);
    return parsed.isValid ? parsed.toFormat("yyyy-MM-dd") : null;
  }
  return null;
}

/** Normalize Mobiscroll timepicker values to `HH:mm` for the colleague floor session. */
export function toColleagueFloorTimeInputValue(value: unknown): string | null {
  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toFormat("HH:mm");
  }
  if (typeof value === "string") {
    const directTime = value.match(/^\d{2}:\d{2}/)?.[0];
    if (directTime) {
      return directTime;
    }
    const parsed = DateTime.fromISO(value);
    return parsed.isValid ? parsed.toFormat("HH:mm") : null;
  }
  return null;
}

/** One hover-tooltip row for a desk occupancy booking (name + time). */
export type ColleagueDeskTooltipLine = {
  id: string;
  name: string;
  time: string;
};

/** Lines for the desk hover tooltip; empty when the desk has no occupancy rows. */
export function buildColleagueDeskTooltipLines(
  bookings: ConnectedBookingData[],
  timeFormat: string,
  unknownTimeLabel: string
): ColleagueDeskTooltipLine[] {
  return bookings.map(booking => {
    const timeLabel = formatColleagueOccupancyTimeRange(booking, timeFormat);
    return {
      id: `${booking.userId}-${booking.bookingId ?? `${booking.start ?? ""}-${booking.end ?? ""}`}`,
      name: colleagueSearchLabel(booking),
      time: timeLabel === "" ? unknownTimeLabel : timeLabel
    };
  });
}

export function buildColleaguePlaceBookingDto(args: {
  user: Pick<TeamMember, "userId" | "firstName" | "surname">;
  locationInventoryId: number;
  inventoryId: number;
  /** Place type of the clicked object. Omitted for a desk. */
  placeTypeId?: number;
  date: string;
  startTime: string;
  endTime: string;
}): PlaceBookingDto {
  return {
    users: [
      {
        userId: args.user.userId,
        firstName: args.user.firstName,
        surname: args.user.surname,
        bookingInventoryId: args.inventoryId,
        isExternal: false
      }
    ],
    bookingType: colleagueFloorPlaceBookingType(args.placeTypeId),
    locationInventoryId: args.locationInventoryId,
    automatedSeating: false,
    automatedSeatingObjects: [],
    weekdays: defaultRecurrenceWeekdays,
    frequence: Frequence.SINGLE,
    startDate: args.date,
    endDate: args.date,
    startTime: args.startTime,
    endTime: args.endTime
  };
}

/**
 * Conference room payload for a booking started on the colleague floor.
 * Same write as the conference form (`newNoPlaceZoneBooking`), not a desk booking.
 */
export function buildColleagueConferenceBookingDto(args: {
  user: Pick<TeamMember, "userId" | "firstName" | "surname" | "email">;
  locationInventoryId: number;
  locationName?: string;
  timezone: string;
  date: string;
  startTime: string;
  endTime: string;
  draft: ColleagueFloorConferenceDraft;
}): ConferenceBookingDto {
  const inputs = {
    mode: BookingType.CONFERENCEZONE,
    isMobileWorking: false,
    timezone: args.timezone,
    selectedLocation: args.locationInventoryId,
    locationName: args.locationName
  } as NotSerializedBookingInputs;
  const booker: TeamMember = {
    userId: args.user.userId,
    firstName: args.user.firstName,
    surname: args.user.surname,
    email: args.user.email,
    bookingInventoryId: args.draft.inventoryId,
    isExternal: false
  };

  return {
    users: transformBookingUsers([booker], args.user.userId, inputs, args.timezone, true),
    bookingType: BookingType.CONFERENCEZONE,
    locationInventoryId: args.locationInventoryId,
    subject: args.draft.subject.trim(),
    description: args.draft.description.trim() === "" ? undefined : args.draft.description.trim(),
    conferenceZoneBookingGuests: args.draft.guests,
    conferenceZoneVisitorRegistrations: buildConferenceVisitorRegistrationDtos(
      args.draft.visitorRegistrations,
      conferenceRoomLocationFromInputs(inputs)
    ),
    onlineMeetingProvider: args.draft.onlineMeetingProvider,
    bookingServicesDto: handleToBookingServiceDto(
      args.draft.inventoryId,
      args.draft.conferenceService
    ),
    frequence: Frequence.SINGLE,
    startDate: args.date,
    endDate: args.date,
    startTime: args.startTime,
    endTime: args.endTime
  };
}
